import { Injectable } from '@angular/core';
import { BroadcastService, MsalGuard, MsalService } from '@azure/msal-angular';
import { isEmbeddedApp } from 'lqx-react';
import { AuthenticationParameters, AuthError, AuthResponse } from 'msal';
import { StringDict } from 'msal/lib-commonjs/MsalTypes';
import { Observable, Subject, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay } from 'rxjs/operators';
import { parentAuthToken, parentUserEntitlements } from '../../../api/embedded-auth';
import { ClaimDict } from '../models/claimType';
import { User, UserAccessDetail } from '../models/user';
import { DigiProductCode, UserEntitlementCatalogValue } from '../models/UserEntitlements';
import { AppConfigService } from './app-config.service';
import { AuthService } from './auth.service';

interface MsalItemBase<T extends string, R> {
  readonly type: T;
  readonly payload: R;
}

type MsalItem = MsalItemBase<'msal:acquireTokenSuccess', AuthResponse>
  | MsalItemBase<'msal:loginSuccess', AuthResponse>
  | MsalItemBase<'msal:loginFailure', AuthError>
  | MsalItemBase<'msal:acquireTokenFailure', AuthError>

@Injectable()
export class MSALServiceProvider extends AuthService {

  accessToken: string;
  userEntitlements: UserEntitlementCatalogValue[];
  private authConfig = this.appConfig.getConfig().msalAuth;
  private subscriptions: Subscription[] = [];
  private _user$: Observable<User>;

  public get user$() {
    return this._user$;
  }


  constructor(private msalService: MsalService, private appConfig: AppConfigService, private msalGuard: MsalGuard, private broadcastService: BroadcastService) {
    super();
    this.setupForgotPasswordStuff();
    this.simultaneousLoginWorkaround();
    this.setupUserObservable();
  }

  private simultaneousLoginWorkaround() {
    this.msalService.handleRedirectCallback((authError, response) => {
      // ddeutsch: this is needed to prevent auth lib from throwing exceptions
      // when a login is triggered twice. Will fix the issue in develop by removing call to
      // login() in app component, and not using msal guard in our routes where we should be using some kind of role guard
      console.log("handleRedirectCallback", authError, response);
    });
  }


  private setupUserObservable() {
    let message$ = this.broadcastService.getMSALItem() as Observable<MsalItem>;
    let claims$ = message$.pipe(
      map(msg => msg.type === 'msal:acquireTokenSuccess' ? msg.payload : null),
      filter(msg => msg && !!msg.idToken),
      distinctUntilChanged((x, y) => x.idToken.rawIdToken == y.idToken.rawIdToken),
      map(msg => (msg.idTokenClaims as unknown) as ClaimDict)
    );
    
    this._user$ = claims$.pipe(
      map(MSALServiceProvider.createUser),
      shareReplay(1)
    );

    // lots of code accesses our non-observable user, so set it here
    this.subscriptions.push(this._user$.subscribe(u => this.user = u));
  }

  private static createUser(idToken: ClaimDict) {
    let result = new User();

    if (idToken.extension_ProductCodes !== undefined) {
      var productCodes = idToken.extension_ProductCodes.split(',');
      var participantIds = idToken.extension_Participants.split(',');
      var participantRoles = idToken.extension_ParticipantRoles.split(',');
      var extensionRoles = idToken.extension_Roles.split(',');
      var platformCodes = idToken.extension_PlatformCodes.split(',');

      var userAccessDetails = new Array<UserAccessDetail>();
      for (let i = 0; i < productCodes.length; i++) {
        if (productCodes[i] === '') {
          continue;
        }
        let userAccessDetail = new UserAccessDetail;

        userAccessDetail.platform_code = platformCodes[i];
        userAccessDetail.product_code = productCodes[i];
        userAccessDetail.participant_id = Number(participantIds[i]);
        userAccessDetail.participant_role = participantRoles[i];
        userAccessDetail.extension_role = extensionRoles[i];

        userAccessDetails.push(userAccessDetail);
      }
      result.userAccessDetails = userAccessDetails;
    }
    result.given_name = idToken.given_name;
    result.family_name = idToken.family_name;
    result.emails = idToken.emails;
    result.uuid = idToken.sub;

    localStorage.setItem('b2c_user', JSON.stringify(result));
    return result;
  }

  getUser(): User {
    return this.user;
  }


  private setupForgotPasswordStuff() {
    this.subscriptions.push(this.broadcastService.subscribe('msal:loginSuccess', (payload) => {
      if (payload.idToken.claims['tfp'] === this.authConfig.forgotPasswordPolicy) {
        // ddeutsch todo: figure out if this is necessary
        return this.msalService.logout();
      }
      this.saveAccessTokenToCache(payload.idToken.rawIdToken);
    }));

    this.subscriptions.push(this.broadcastService.subscribe('msal:loginFailure', payload => {
      if (payload.errorMessage.indexOf('AADB2C90118') > -1) {
        // see https://docs.microsoft.com/en-us/azure/active-directory-b2c/user-flow-overview#linking-user-flows
        var forgotPwdAuthority = this.authConfig.forgotPasswordAuthority;
        this.msalService.authority = forgotPwdAuthority;
      }
    }));
  }

  login() {
      const loginRequest = {
        prompt: 'select_account',
        extraQueryParameters: this.getExtraQueryParameters()
      };

      this.msalService.loginRedirect(loginRequest);
  }

  async ensureLoggedIn() {
    const requestObj: AuthenticationParameters = {
      scopes: [this.authConfig.clientId],
      authority: this.authConfig.authority,
      extraQueryParameters: this.getExtraQueryParameters()
   };

    try {      
      if(isEmbeddedApp()){
        this.accessToken = await parentAuthToken;
        const user = MSALServiceProvider.createUser(((JSON.parse(atob(this.accessToken.split('.')[1])) as unknown) as ClaimDict));
        localStorage.setItem('b2c_user', JSON.stringify(user));

        const entitlements = await parentUserEntitlements;
        localStorage.setItem('userEntitlements', JSON.stringify(entitlements));
      }
      else{
        const authResponse = await this.msalService.acquireTokenSilent(requestObj);
        this.accessToken = authResponse.idToken.rawIdToken;

        localStorage.setItem("loggedIn", "true");
      }

      this.saveAccessTokenToCache(this.accessToken);

      return true;
    } catch {
      this.login();
      // since the login() call above will redirect to the login page, return a promise that never resolves so whoever is calling
      // this function does not proceed to do anything else before the redirect happens
      await new Subject().toPromise();
      return false;
    }
  }


  msalLogout() {
    this.msalService.logout();
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
    sessionStorage.clear();
  }

  private getExtraQueryParameters(): StringDict {
    const params: StringDict = {};
    params['platformCodes'] = 'lqx';
    params['uiTheme'] = 'inblock';
    params['DomainHint'] = 'none';
    return params;
  }
}
